import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  IconButton,
} from "@material-ui/core";
import { Add as AddIcon, Delete as DeleteIcon } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(theme => ({
  section: {
    padding: "16px",
    paddingTop: "8px",
    paddingLeft: "0",
    paddingRight: "16px",
    maxHeight: "calc(100vh - 140px)",
    overflowY: "auto",
    maxWidth: "100%",
    [theme.breakpoints.down("sm")]: {
      padding: "15px",
      backgroundColor: "#1a1a1a",
      paddingBottom: "86px",
      height: "calc(100vh - 80px)",
      paddingRight: "15px",
    },
  },
  sectionTitle: {
    color: "#f06ec4",
    fontWeight: 600,
    fontSize: "24.5px",
    marginBottom: "3px",
  },
  infoText: {
    color: "white",
    fontSize: "11px",
    marginBottom: "30px",
  },
  contentCard: {
    backgroundColor: "#1E1E1E",
    borderRadius: "8px",
    marginBottom: "16px",
    border: "1px solid #363636",
  },
  cardContent: {
    padding: "20px !important",
  },
  contentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  contentType: {
    color: "#f06ec4",
    fontSize: "18px",
    fontWeight: 600,
  },
  formRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
    gap: "16px",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "12px",
      width: "100%",
    },
  },
  label: {
    color: "#fff",
    fontSize: "14px",
    fontWeight: 500,
    minWidth: "120px",
    [theme.breakpoints.down("sm")]: {
      minWidth: "auto",
      fontSize: "16px",
      marginBottom: "4px",
    },
  },
  inputField: {
    flex: 1,
    "& .MuiInputBase-root": {
      color: "white",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EDECED",
      },
      "&:hover fieldset": {
        borderColor: "#f06ec4",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f06ec4",
      },
      "& input[type=number]": {
        "-moz-appearance": "textfield",
        "&::-webkit-outer-spin-button": {
          "-webkit-appearance": "none",
          margin: 0,
        },
        "&::-webkit-inner-spin-button": {
          "-webkit-appearance": "none",
          margin: 0,
        },
      },
    },
  },
  selectField: {
    flex: 1,
    "& .MuiSelect-root": {
      color: "white",
    },
    "& .MuiSelect-icon": {
      color: "#f06ec4",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EDECED",
      },
      "&:hover fieldset": {
        borderColor: "#f06ec4",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f06ec4",
      },
    },
  },
  switchControl: {
    "& .MuiFormControlLabel-root": {
      marginLeft: "0px !important",
      marginRight: "0px !important",
    },
    "& .MuiFormControlLabel-label": {
      color: "white",
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#f06ec4",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#f06ec4",
    },
  },
  addButton: {
    backgroundColor: "#f06ec4",
    color: "white",
    marginTop: "16px",
    marginBottom: "16px",
    "&:hover": {
      backgroundColor: "#d45ca5",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  saveButton: {
    backgroundColor: "#f06ec4",
    color: "white",
    float: "right",
    marginTop: "20px",
    "&:hover": {
      backgroundColor: "#d45ca5",
    },
    [theme.breakpoints.down("sm")]: {
      float: "none",
      width: "100%",
      marginTop: "16px",
    },
  },
  deleteButton: {
    color: "#ff4444",
    "&:hover": {
      backgroundColor: "rgba(255, 68, 68, 0.1)",
    },
  },
}));

interface PayPerViewRate {
  id: string;
  contentType: "video" | "photo" | "message" | "audio";
  price: string;
  hasTimer: boolean;
  timerHours: string;
}

const PayPerViewRates: React.FC = () => {
  const classes = useStyles();
  const [rates, setRates] = useState<PayPerViewRate[]>([
    {
      id: "1",
      contentType: "video",
      price: "",
      hasTimer: false,
      timerHours: "",
    },
  ]);

  const addRate = () => {
    const newRate: PayPerViewRate = {
      id: Date.now().toString(),
      contentType: "video",
      price: "",
      hasTimer: false,
      timerHours: "",
    };
    setRates([...rates, newRate]);
  };

  const deleteRate = (id: string) => {
    setRates(rates.filter(rate => rate.id !== id));
  };

  const updateRate = (id: string, field: keyof PayPerViewRate, value: any) => {
    setRates(
      rates.map(rate => (rate.id === id ? { ...rate, [field]: value } : rate))
    );
  };

  const handleSave = () => {
    // TODO: Implement API call
  };

  const getContentTypeLabel = (type: string) => {
    switch (type) {
      case "video":
        return "Video";
      case "photo":
        return "Photo";
      case "message":
        return "Message";
      case "audio":
        return "Audio";
      default:
        return type;
    }
  };

  return (
    <div className={classes.section}>
      <Typography className={classes.sectionTitle}>
        Pay-Per-View Rates
      </Typography>
      <Typography className={classes.infoText}>
        Set pricing for individual content access. Users will pay these rates to
        unlock specific posts, videos, or messages.
      </Typography>

      {rates.map(rate => (
        <Card key={rate.id} className={classes.contentCard}>
          <CardContent className={classes.cardContent}>
            <Box className={classes.contentHeader}>
              <Typography className={classes.contentType}>
                {getContentTypeLabel(rate.contentType)}
              </Typography>
              {rates.length > 1 && (
                <IconButton
                  className={classes.deleteButton}
                  onClick={() => deleteRate(rate.id)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>

            <Box className={classes.formRow}>
              <Typography className={classes.label}>Content Type</Typography>
              <FormControl
                className={classes.selectField}
                variant="outlined"
                size="small"
              >
                <Select
                  value={rate.contentType}
                  onChange={e =>
                    updateRate(rate.id, "contentType", e.target.value)
                  }
                  MenuProps={{
                    PaperProps: {
                      style: {
                        backgroundColor: "#1E1E1E",
                        color: "white",
                      },
                    },
                  }}
                >
                  <MenuItem value="video" style={{ color: "white" }}>
                    Video
                  </MenuItem>
                  <MenuItem value="photo" style={{ color: "white" }}>
                    Photo
                  </MenuItem>
                  <MenuItem value="message" style={{ color: "white" }}>
                    Message
                  </MenuItem>
                  <MenuItem value="audio" style={{ color: "white" }}>
                    Audio
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box className={classes.formRow}>
              <Typography className={classes.label}>Price ($)</Typography>
              <TextField
                className={classes.inputField}
                variant="outlined"
                size="small"
                type="number"
                value={rate.price}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateRate(rate.id, "price", e.target.value)
                }
                placeholder="4.99"
              />
            </Box>

            <FormControlLabel
              className={classes.switchControl}
              control={
                <Switch
                  checked={rate.hasTimer}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateRate(rate.id, "hasTimer", e.target.checked)
                  }
                />
              }
              label="Limited access time"
            />

            {rate.hasTimer && (
              <Box className={classes.formRow}>
                <Typography className={classes.label}>
                  Access Timer (hours)
                </Typography>
                <TextField
                  className={classes.inputField}
                  variant="outlined"
                  size="small"
                  type="number"
                  value={rate.timerHours}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateRate(rate.id, "timerHours", e.target.value)
                  }
                  placeholder="24"
                />
              </Box>
            )}
          </CardContent>
        </Card>
      ))}

      <Button
        className={classes.addButton}
        startIcon={<AddIcon />}
        onClick={addRate}
      >
        Add Content Type
      </Button>

      <Button className={classes.saveButton} onClick={handleSave}>
        Save Changes
      </Button>
    </div>
  );
};

export default PayPerViewRates;
